import { type PropsWithChildren, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { BUBBLECHAT_WEB_PATH } from '@repo/constants';
import { setTokenOnHeader } from '@repo/services/api';
import { getCustomToken } from '@repo/services/hooks';
import { checkIsAosDevice } from '@repo/utils';
import { useQueryClient } from '@tanstack/react-query';
import { User, onAuthStateChanged, signInWithCustomToken } from 'firebase/auth';

import { auth } from 'services/firebase';
import http, { axiosInstance } from 'services/http';
import { getTokenFromCookie, removeTokenCookie, setTokenCookie } from 'utils/auth';

import { PATH } from 'routers/paths';

const AuthGuard = ({ children }: PropsWithChildren) => {
  const cookieDomain = import.meta.env.VITE_APP_COOKIE_DOMAIN;
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const isCustomLogin = localStorage.getItem('isCustomLogin') === 'true';
  const isAosDevice = checkIsAosDevice();

  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Get redirectURL from location state or default to main path
  const redirectURL = (location.state as { redirectURL?: string })?.redirectURL ?? PATH.MAIN;
  // 로그인 성공 공통 로직
  const handleLoginInFirebaseAuth = async (user: User, token: string) => {
    setUser(user);
    setTokenCookie(token, cookieDomain);
    setTokenOnHeader(axiosInstance, auth, token);
    localStorage.setItem('isCustomLogin', 'true');
    queryClient.resetQueries();
    navigate(redirectURL);
  };

  // 쿠키를 사용한 로그인 처리
  const handleLoginWithCookie = async (cookieToken: string) => {
    try {
      setTokenOnHeader(axiosInstance, auth, cookieToken);
      const res = await getCustomToken(http);
      if (res?.token) {
        const userCredential = await signInWithCustomToken(auth, res.token);
        const idToken = await userCredential.user.getIdToken();
        await handleLoginInFirebaseAuth(userCredential.user, idToken);
      } else {
        removeTokenCookie(cookieDomain);
        setUser(null);
      }
    } catch (error) {
      console.error('Token validation failed:', error);
      removeTokenCookie(cookieDomain);
      localStorage.removeItem('isCustomLogin');

      // 현재 토큰 삭제가 안되는 이슈로 인해 토큰 인증 실패시 리다이렉트 기능을 넣었음 (추후 수정)
      // if (isAosDevice) {
      //   navigate(PATH.AUTH.SIGN_IN);
      //   return;
      // } else {
      //   const redirectUrl = import.meta.env.VITE_APP_CHAT_URL;
      //   window.location.href = `${redirectUrl}/sign-in?callbackUrl=${window.origin}`;
      // }
      setUser(null);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      try {
        // 현재 Firebase 인증 상태 감시
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
          if (currentUser) {
            // 파이어베이스에 이미 로그인 여부가 파악된 경우
            const token = await currentUser.getIdToken();
            await handleLoginInFirebaseAuth(currentUser, token);
          } else {
            // Firebase에 로그인 되지 않은 경우, 쿠키로 인증 체크하기
            const cookieToken = getTokenFromCookie();
            if (cookieToken) {
              await handleLoginWithCookie(cookieToken);
            } else {
              // 쿠키도 없는 경우, 로그인 페이지로 이동
              setUser(null);
              // const isReceiveSuccess = searchParams.get('receive') === 'success';

              if (isAosDevice) {
                navigate(PATH.AUTH.SIGN_IN);
                return;
              } else {
                const redirectUrl = import.meta.env.VITE_APP_CHAT_URL;
                const _redirectUrl = import.meta.env.VITE_APP_ENV === 'local' ? 'http://localhost:3000' : redirectUrl;
                window.location.href = `${_redirectUrl}/sign-in?callbackUrl=${window.origin}`;
              }
            }
          }
          setLoading(false);
        });

        return () => unsubscribe();
      } catch (error) {
        console.error('Auth initialization failed:', error);
        setLoading(false);
      }
    };

    initAuth();
  }, [auth, http, axiosInstance]);

  if (!isCustomLogin) {
    return null;
  }

  return children;
};

export default AuthGuard;
